import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import InfoCard from "../../../components/components/js/InfoCard";
import { Masonry } from "masonic";
import { FaUserCircle } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import AuthContext from "../../../context/AuthProvider";
import RoomsCard from "../../../components/components/js/RoomsCard";
import { DeleteConfirmationDialog } from "../../../components/components/js/DeleteConfirmationDialog";
import { formatAge, formatDate } from "../../../utils/exportUtils";
import { Avatar, Typography, Box, Button, IconButton } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from "react-i18next";
// import { toast } from "react-toastify";

export const StaffDetails = () => {
  const { staffId } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const { school, auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [cardData, setCardData] = useState([]);
  const fileInputRef = useRef();
  const [personalInfo, setPersonalInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);

      const [roomsRes,staffRes, gendersRes, countriesRes] = await Promise.all([
          axiosPrivate.get(API_URLS.getRooms(auth.id, school.id)),
          axiosPrivate.get(API_URLS.getStaffDetails(staffId)),
        axiosPrivate.get(API_URLS.gender),
        axiosPrivate.get(API_URLS.country),
      ]);

      const staffData = staffRes.data.staff;
      const roomsData = staffRes.data.classes;
      const rooms = roomsRes.data;
      const genders = gendersRes.data.genders.map((g) => ({
        id: g.id,
        name: g.Name,
      }));
      const countries = countriesRes.data.map((c) => ({
        id: c.code,
        name: c.name,
      }));

      const currentRooms = roomsData
        ? roomsData.map((room) => ({
            id: room.id,
            room: room.ClassName,
          }))
        : [];
      const allRooms = rooms.map((room) => ({
        id: room.id,
        room: room.ClassName,
      }));

      const last_name = staffData.LastName ?? "";
      const name = staffData.FirstName + " " + last_name;

      setPersonalInfo({
        name: name,
        image: staffData.ProfileImg,
      });

      const personal = [
        { 
          label: "First Name", 
          value: staffData.FirstName || "", 
          editable: true 
        },
        { 
          label: "Last Name", 
          value: staffData.LastName || "", 
          editable: true 
        },
        {
          label: "BirthDate",
          value: staffData.BirthDate,
          type: "date",
          editable: true,
        },
        {
          label: "Age",
          value: formatAge(staffData.BirthDate),
          editable: false,
        },
        { label: "Email", value: staffData.email, editable: true },
        { label: "Phone", value: staffData.Phone, editable: true },
        {
          label: "Gender",
          value: staffData.gender_id,
          type: "select",
          options: genders,
          editable: true,
        },
        {
          label: "Country",
          value: staffData.countries,
          type: "select",
          options: countries,
          editable: true,
        },
        { label: "Allergies", value: staffData.Allergies, editable: true },
        { label: "Notes", value: staffData.Notes, editable: true },
        { label: "Medications", value: staffData.Medications, editable: true },
        { label: "Doctor Name", value: staffData.DoctorName, editable: true },
        {
          label: "Doctor Phone No.",
          value: staffData.DoctorPhone,
          editable: true,
        },
      ];

      const address = [
        { label: "Street", value: staffData.Street, editable: true },
        {
          label: "Appartment",
          value: staffData.AppartmentHouse,
          editable: true,
        },
        { label: "City", value: staffData.City, editable: true },
        { label: "State", value: staffData.State, editable: true },
        { label: "Zip Code", value: staffData.ZipCode, editable: true },
      ];

      const certificates = [
        {
          label: "Certificate",
          value: staffData.CertCertification,
          editable: true,
        },
        {
          label: "Child Education Credits",
          value: staffData.CertChildEduCredits,
          editable: true,
        },
        {
          label: "Infant Toddler Credits",
          value: staffData.CertInfantToddlerCredits,
          editable: true,
        },
        {
          label: "Certificate Notes",
          value: staffData.CertNotes,
          editable: true,
        },
      ];

      const employmentInfo = [
        {
          label: "Status",
          value: staffData.StatusStaff,
          type: "select",
          options: [
            { id: "Active", name: "Active" },
            { id: "Inactive", name: "In Active" },
            { id: "Past", name: "Past" },
          ],
          editable: true,
        },
        { label: "Role", value: staffData.Role, editable: true },
        {
          label: "Hire Date",
          value: formatDate(staffData.EmpHireDate),
          editable: true,
          type: "date",
        },
        {
          label: "Employment Notes",
          value: staffData.EmpNotes,
          editable: true,
        },
      ];

      const emergencyContact = [
        {
          label: "Emergency Name",
          value: staffData.EmergencyContactFirstName,
          editable: true,
        },
        {
          label: "Emergency Type",
          value: staffData.EmergencyContactType,
          editable: true,
        },
        {
          label: "Emergency Phone",
          value: staffData.EmergencyContactPhone,
          editable: true,
        },
      ];

      const finalData = [
        { type: "info", title: "Personal Info", data: personal },
        { type: "info", title: "Employment Info", data: employmentInfo },
        {
          type: "rooms",
          allRooms: allRooms,
          initialSelected: currentRooms,
          fullWidth: true,
        },
        { type: "info", title: "Emergency Contact", data: emergencyContact },
        { type: "info", title: "Address", data: address, fullWidth: true },
        {
          type: "info",
          title: "Certificate",
          data: certificates,
          fullWidth: true,
        },
      ];

      setCardData(finalData);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        console.error("Error fetching data", err);
        setError("Failed to load student data.");
      }
    } finally {
      setLoading(false);
    }
  }, [staffId, auth.id, axiosPrivate, school.id]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      await axiosPrivate.delete(API_URLS.getStaffDetails(staffId));
      navigate(-1);
    } catch (error) {
      console.error("Delete request error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateImage = async (img) => {
      try {
        setLoading(true);
        setError(null);
        const formData = new FormData();
    formData.append("ProfileImg", img); 
        const response = await axiosPrivate.patch(
          API_URLS.getStaffDetails(staffId),
          formData,
          {headers: { "Content-Type": "multipart/form-data" } }
        );
        console.log("Patch success", response);
        navigate(0);
      } catch (err) {
        console.error("Patch error", err);
        setError("Failed to update. Please try again.");
      } finally {
        setLoading(false);
      }
    };

  const handleEditClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.readAsDataURL(file);

        updateImage(file);
    }
  };

  const fullWidthCards = cardData.filter((card) => card.fullWidth);
  const masonryCards = cardData.filter((card) => !card.fullWidth);


    if (notFound) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="80vh"
          textAlign="center"
        >
          <Typography variant="h4" color="primary" gutterBottom>
            {t(`staffDetails.notFound.title`)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t(`staffDetails.notFound.description`)}
          </Typography>
        </Box>
      );
    }

  return (
    <div
      className={`p-5 d-block ms-auto px-4`}
      style={{
        minHeight: "70vh",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: "5%", pr: "35px" }}
      >
        <Box display="flex" alignItems="center" gap={2}>
      <Box position="relative" display="inline-block">
        <Box

          sx={(theme)=>({
            position: "absolute",
            top: -4,
            left: -4,
            width: 93,
            height: 93,
            borderRadius: "50%",
            border: `1px solid ${theme.palette.primary.main}`, 
            pointerEvents: "none", 
          })}
        />
        {personalInfo.image ? (
          <Avatar
            src={personalInfo.image}
            alt="img"
            sx={{ width: 85, height: 85 }}
          />
        ) : (
          <FaUserCircle size={85} />
        )}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <IconButton
          size="small"
          sx={(theme)=>({
            position: "absolute",
            bottom: 0,
            right: 0,
            width:25,
            height:25,
            backgroundColor: "white",
            borderRadius: "50%",
            boxShadow: 1,
            border: `1px solid ${theme.palette.primary.main}`,
            "&:hover": { backgroundColor: "#e3f2fd" },
          })}
          aria-label="edit"
        >
          <EditIcon fontSize="small" color="primary" onClick={handleEditClick}/>
        </IconButton>
      </Box>

      <Typography variant="h5" fontWeight="bold">
        {personalInfo?.name}
      </Typography>
    </Box>

        <Button
          variant="text"
          onClick={() => setShowDeleteConfirm(true)}
          sx={{
            color: "red",
            fontWeight: "bold",
            fontSize:"1.1rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          {t(`staffDetails.deleteButton`)}
        </Button>
      </Box>

      <div style={{ padding: "35px" }}>
        <Masonry
          items={masonryCards}
          columnGutter={18}
          columnWidth={400}
          render={({ data }) => (
            <div>
              {data.type === "info" ? (
                <InfoCard
                  title={data.title}
                  editable
                  data={data.data}
                  id={staffId}
                  type={"staff"}
                  onSaveSuccess={fetchAllData}
                />
              ) : (
                <RoomsCard
                  allRooms={data.allRooms}
                  initialSelected={data.initialSelected}
                  id={staffId}
                />
              )}
            </div>
          )}
        />
        {fullWidthCards.map((data, idx) => (
          <div key={`full-${idx}`}>
            {data.type === "rooms" ? (
              <RoomsCard
                allRooms={data.allRooms}
                initialSelected={data.initialSelected}
                id={staffId}
              />
            ) : (
              <InfoCard
                title={data.title}
                editable
                data={data.data}
                id={staffId}
                type={"staff"}
                onSaveSuccess={fetchAllData}
              />
            )}
          </div>
        ))}
        {showDeleteConfirm && (
          <DeleteConfirmationDialog
            onDelete={handleDelete}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}
      </div>
    </div>
  );
};
